/**
 * Defense-in-depth PII redaction for Sentry event payloads.
 *
 * Walks an arbitrary object tree and replaces values at any depth whose key
 * matches PII_KEYS with the literal string `[redacted]`. Used by the
 * `beforeSend` and `beforeBreadcrumb` filters in `apps/{admin,mobile}/src/sentry.ts`
 * to enforce the privacy commitment in `docs/compliance/privacy-policy.md`
 * §3.7 and §7.5: stack traces only, no user-identifying fields.
 *
 * Invariant tested in `packages/shared/tests/scrubPii.test.ts`. If a new
 * user-identifying field is added to either app's runtime state, add its key
 * here so the next Sentry event keeps the privacy contract intact.
 */
export const PII_KEYS = new Set([
  'email',
  'publicEmail',
  'contactEmail',
  'phone',
  'phoneNumber',
  'contactPhone',
  'displayName',
  'firstName',
  'lastName',
  'name',
  'bio',
  'publicBio',
  'linkedIn',
  'publicLinkedIn',
  'website',
  'publicWebsite',
  'organization',
  'publicOrganization',
  'workCity',
  'workState',
  'title',
  'publicTitle',
  'dietaryRestrictions',
  'accessibilityNeeds',
  'pushTokens',
  'token',
  'idToken',
  'accessToken',
  'refreshToken',
  'fcmToken',
]);

export function scrubPii(obj: unknown): unknown {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(scrubPii);
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (PII_KEYS.has(key)) {
      out[key] = '[redacted]';
    } else {
      out[key] = scrubPii(value);
    }
  }
  return out;
}
